/**
 * YouTube URL helpers.
 *
 * The hosts recognized here must match the backend changeset's host
 * allowlist exactly, since a video the server accepted as "youtube" must
 * always be renderable here.
 */

const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtu.be",
]);

/**
 * Extracts the 11-character video id from a YouTube watch or short URL.
 * Returns `null` when the URL is malformed, isn't a recognized YouTube
 * host, or carries no identifiable video id.
 */
export function extractYoutubeVideoId(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const host = parsed.hostname.toLowerCase();
  if (!YOUTUBE_HOSTS.has(host)) {
    return null;
  }

  if (host === "youtu.be") {
    const id = parsed.pathname.replace(/^\//, "").split("/")[0];
    return id || null;
  }

  const id = parsed.searchParams.get("v");
  if (id) return id;

  const embedMatch = parsed.pathname.match(/\/(?:embed|v)\/([^/]+)/);
  return embedMatch?.[1] ?? null;
}
